import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * react-router's `setSearchParams(fn)` always calls `fn` with the params captured at
 * the last render, so several updates fired from one event handler (e.g. "Reset filters",
 * which clears a dozen filters in a row) each start from the same snapshot and the last
 * one wins — every other filter silently survives the reset.
 *
 * To make consecutive updates compose, the params written during the current tick are
 * buffered here and used as the base for the next update; the buffer is dropped on the
 * next microtask, so normal (separate-tick) updates still read from the live URL.
 */
let pendingParams: URLSearchParams | null = null;
let pendingScheduled = false;

function basePendingParams(prev: URLSearchParams): URLSearchParams {
  return new URLSearchParams(pendingParams ?? prev);
}

function rememberPendingParams(params: URLSearchParams) {
  pendingParams = new URLSearchParams(params);
  if (!pendingScheduled) {
    pendingScheduled = true;
    queueMicrotask(() => {
      pendingParams = null;
      pendingScheduled = false;
    });
  }
}

/**
 * useState-compatible hook that persists a value in the URL query string.
 * The value is omitted from the URL when it equals `defaultValue` (deep-equal for arrays/objects).
 *
 * @param key      URL search-param key
 * @param defaultValue Default value used when the param is absent
 * @param serialize    Convert value -> string (default: JSON for non-strings)
 * @param deserialize  Convert string -> value (default: JSON parse for non-strings)
 */
export function useUrlFilterState<T>(
  key: string,
  defaultValue: T,
  serialize?: (v: T) => string | null,
  deserialize?: (s: string) => T,
): [T, (v: T | ((prev: T) => T)) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const isString = typeof defaultValue === "string";
  const _serialize =
    serialize ??
    ((v: T) => {
      if (v === null || v === undefined) return null;
      if (isString) return v as unknown as string;
      return JSON.stringify(v);
    });
  const _deserialize =
    deserialize ??
    ((s: string): T => {
      if (isString) return s as unknown as T;
      try {
        return JSON.parse(s) as T;
      } catch {
        return defaultValue;
      }
    });

  const raw = searchParams.get(key);
  const value = useMemo<T>(() => {
    if (raw === null) return defaultValue;
    return _deserialize(raw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setSearchParams(
        (prev) => {
          const params = basePendingParams(prev);
          const current = params.get(key);
          const currentVal: T = current === null ? defaultValue : _deserialize(current);
          const resolved = typeof next === "function" ? (next as (p: T) => T)(currentVal) : next;
          const serialized = _serialize(resolved);
          const defaultSerialized = _serialize(defaultValue);
          if (serialized === null || serialized === defaultSerialized) {
            params.delete(key);
          } else {
            params.set(key, serialized);
          }
          rememberPendingParams(params);
          return params;
        },
        { replace: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, setSearchParams],
  );

  return [value, setValue];
}

/** Comma-separated string-array variant. Defaults to ["all"]; omitted from URL when value equals default. */
export function useUrlArrayFilterState(
  key: string,
  defaultValue: string[] = ["all"],
): [string[], (v: string[] | ((prev: string[]) => string[])) => void] {
  return useUrlFilterState<string[]>(
    key,
    defaultValue,
    (v) => {
      if (!v || v.length === 0) return null;
      if (v.length === defaultValue.length && v.every((x, i) => x === defaultValue[i])) return null;
      return v.join(",");
    },
    (s) => (s ? s.split(",") : defaultValue),
  );
}
